// SSE client for /api/v1/chat. Uses fetch-event-source so we can set
// the Authorization header (standard EventSource cannot). Typed handlers
// per PRD event — Phase 3 adds tool_call/tool_result/reasoning.
//
// 401 handling: this transport bypasses the axios interceptor that
// normally refreshes expired tokens. We replicate the refresh path
// here — onopen sees a 401, we call /api/v1/auth/refresh once via the
// shared axios client (which writes the new tokens to storage), then
// re-attempt the chat request with the new access token. A single
// retry — the refresh path itself can fail (refresh expired), in which
// case we surface "session expired" so the UI redirects to /login.

import { fetchEventSource } from '@microsoft/fetch-event-source';
import axios from 'axios';
import { BASE_URL } from './api';
import { storage } from './storage';
import type {
  ChangeDecision, ClarifyPayload, DiffDeltaPayload, DiffReadyPayload,
  DonePayload, ErrorPayload, ReasoningPayload, SSEEventType,
  TextPayload, ThinkingPayload, ToolCallPayload, ToolResultPayload,
  UsagePayload, WorkbookContext,
} from '@/types/chat';
import type { LoginResponse } from '@/types/api';
import { api } from './api';

export interface ChatStreamHandlers {
  onThinking?: (p: ThinkingPayload) => void;
  onText?: (p: TextPayload) => void;
  onReasoning?: (p: ReasoningPayload) => void;
  onToolCall?: (p: ToolCallPayload) => void;
  onToolResult?: (p: ToolResultPayload) => void;
  onDiffDelta?: (p: DiffDeltaPayload) => void;
  onDiffReady?: (p: DiffReadyPayload) => void;
  onClarify?: (p: ClarifyPayload) => void;
  onUsage?: (p: UsagePayload) => void;
  onDone?: (p: DonePayload) => void;
  onError?: (p: ErrorPayload | { code: string; message: string }) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

export interface ChatStreamRequest {
  message: string;
  conversation_id?: string;
  workbook_hash?: string;
  workbook_context?: WorkbookContext;
  images?: ChatImage[];
  attachment_ids?: string[];
}

export interface ChatImage {
  media_type: 'image/png' | 'image/jpeg' | 'image/webp' | 'image/gif';
  data: string; // raw base64, no data: prefix
}

export interface ChatStreamController {
  abort: () => void;
}

class FatalError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
  }
}

// refreshAccess calls /api/v1/auth/refresh and writes the new tokens
// to storage. Returns the new access token. Throws when refresh fails
// (refresh expired / revoked) — caller should treat that as full logout.
async function refreshAccess(): Promise<string> {
  const refresh = storage.getRefresh();
  if (!refresh) throw new Error('no refresh token');
  const resp = await axios.post<LoginResponse>(
    `${BASE_URL}/api/v1/auth/refresh`,
    { refresh_token: refresh },
    { headers: { 'Content-Type': 'application/json' } },
  );
  storage.setTokens(resp.data.access_token, resp.data.refresh_token);
  storage.setUser(resp.data.user);
  return resp.data.access_token;
}

export function streamChat(
  req: ChatStreamRequest,
  handlers: ChatStreamHandlers,
): ChatStreamController {
  const abort = new AbortController();

  // openOnce kicks off the SSE call with the given access token. We
  // call it twice at most: once with the cached token, and (if that
  // 401s on an expired token) once again after a successful refresh.
  let didRetryAfterRefresh = false;

  const openOnce = (accessToken: string | null) => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    };
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

    fetchEventSource(`${BASE_URL}/api/v1/chat`, {
      method: 'POST',
      headers,
      body: JSON.stringify(req),
      signal: abort.signal,
      openWhenHidden: true,

      async onopen(response) {
        if (response.ok) {
          handlers.onOpen?.();
          return;
        }
        // 401 from /chat almost always means the access token expired
        // mid-session. Refresh once and re-open the stream — this is
        // the same recovery the axios interceptor does for non-SSE
        // requests. We only retry ONCE so a hard-fail (refresh also
        // expired) surfaces cleanly to the UI instead of looping.
        if (response.status === 401 && !didRetryAfterRefresh) {
          didRetryAfterRefresh = true;
          let body: { error?: string; message?: string } | undefined;
          try { body = await response.json(); } catch { /* non-JSON */ }
          const code = body?.error;
          // Treat ANY 401 as refresh-eligible when we have a refresh
          // token cached. Earlier we restricted to known codes, but a
          // network proxy / nginx 401 with no body would skip refresh
          // entirely and strand the user. Bad codes still fail closed
          // because refreshAccess will throw if our refresh is invalid.
          const refreshable = !code ||
            code === 'expired_token' || code === 'invalid_token' || code === 'unauthorized';
          if (refreshable && storage.getRefresh()) {
            // CRITICAL: do NOT wrap the success path in the same try
            // as refreshAccess(). The sentinel `__refreshed__` throw
            // below MUST escape this catch — earlier code put it
            // inside try so the catch ate it and ran the "logout"
            // branch even when refresh had just succeeded.
            let newToken: string;
            try {
              newToken = await refreshAccess();
            } catch (refreshErr) {
              // Refresh itself failed — treat as full session expiry.
              storage.clear();
              window.dispatchEvent(new CustomEvent('auth:expired'));
              throw new FatalError(401, 'session expired — please sign in again');
            }
            // Refresh succeeded. Schedule a fresh stream open with the
            // new token, then close THIS stream by throwing the
            // sentinel — onerror recognises it and exits cleanly
            // without surfacing an error to the caller.
            setTimeout(() => openOnce(newToken), 0);
            throw new FatalError(401, '__refreshed__');
          }
        }
        let message = `HTTP ${response.status}`;
        try {
          const errBody = await response.json();
          if (errBody?.message) message = errBody.message;
        } catch {
          // non-JSON body; keep generic message
        }
        throw new FatalError(response.status, message);
      },

      onmessage(ev) {
        const name = (ev.event || '') as SSEEventType | '';
        if (!name || !ev.data) return;
        let payload: unknown;
        try {
          payload = JSON.parse(ev.data);
        } catch {
          return;
        }
        switch (name) {
          case 'thinking':
            handlers.onThinking?.(payload as ThinkingPayload);
            break;
          case 'text':
            handlers.onText?.(payload as TextPayload);
            break;
          case 'reasoning':
            handlers.onReasoning?.(payload as ReasoningPayload);
            break;
          case 'tool_call':
            handlers.onToolCall?.(payload as ToolCallPayload);
            break;
          case 'tool_result':
            handlers.onToolResult?.(payload as ToolResultPayload);
            break;
          case 'diff_delta':
            handlers.onDiffDelta?.(payload as DiffDeltaPayload);
            break;
          case 'diff_ready':
            handlers.onDiffReady?.(payload as DiffReadyPayload);
            break;
          case 'clarify':
            handlers.onClarify?.(payload as ClarifyPayload);
            break;
          case 'usage':
            handlers.onUsage?.(payload as UsagePayload);
            break;
          case 'done':
            handlers.onDone?.(payload as DonePayload);
            break;
          case 'error':
            handlers.onError?.(payload as ErrorPayload);
            break;
        }
      },

      onerror(err) {
        if (err instanceof FatalError) {
          // Special case: the refresh path threw __refreshed__ to
          // close THIS stream so the new token's stream can take over.
          // Don't surface that as a user-visible error.
          if (err.message === '__refreshed__') {
            throw err; // exits this fetchEventSource cleanly
          }
          handlers.onError?.({ code: `http_${err.status}`, message: err.message });
          throw err;
        }
      },

      onclose() {
        handlers.onClose?.();
      },
    }).catch(() => {
      // Fatal errors already surfaced via onerror; swallow the rejection.
    });
  };

  // Kick off the first attempt with whatever access token is cached.
  openOnce(storage.getAccess());

  return {
    abort: () => abort.abort(),
  };
}

// --- Phase 4: confirmation audit -----------------------------------------

export interface ConfirmationRequest {
  conversation_id: string;
  message_id: string;
  decisions: ChangeDecision[];
}

export interface ConfirmationResponse {
  approved: number;
  rejected: number;
}

/**
 * Posts the audit record AFTER Office.js has applied approved changes.
 * Server never touches the workbook; this is purely compliance logging.
 */
export async function confirmChanges(req: ConfirmationRequest): Promise<ConfirmationResponse> {
  const { data } = await api.post<ConfirmationResponse>('/api/v1/chat/confirmations', req);
  return data;
}

// --- Phase 7: conversation history ---------------------------------------

export interface ConversationSummary {
  id: string;
  workbook_hash?: string;
  title?: string;
  provider?: string;
  model?: string;
  created_at: string;
  updated_at: string;
}

export interface ConversationDetail {
  conversation: ConversationSummary;
  messages: Array<{
    id: string;
    role: 'user' | 'assistant' | 'tool';
    content: string;
    tokens_input?: number;
    tokens_output?: number;
    cost_usd?: number;
    created_at: string;
  }>;
}

export interface ListConversationsParams {
  q?: string;
  limit?: number;
  offset?: number;
}

export async function listConversations(
  params: ListConversationsParams = {},
): Promise<{ conversations: ConversationSummary[]; limit: number; offset: number; query: string }> {
  const { data } = await api.get('/api/v1/conversations', { params });
  return data;
}

export async function getConversation(id: string): Promise<ConversationDetail> {
  const { data } = await api.get<ConversationDetail>(`/api/v1/conversations/${id}`);
  return data;
}

export async function deleteConversation(id: string): Promise<void> {
  await api.delete(`/api/v1/conversations/${id}`);
}

export async function renameConversation(id: string, title: string): Promise<void> {
  await api.patch(`/api/v1/conversations/${id}`, { title });
}
